import { useState } from "react"
import { Link } from "@inertiajs/react"
import AppLayout from "@/components/AppLayout"
import PageTitle from "@/components/PageTitle"
import { Card, CardContent, CardHeader } from "@/components/ui/Card"
import { Input } from "@/components/ui/Input"
import { purchase_destinations_path } from "@/lib/routes"
import NeedTable from "./components/NeedTable"
import type { Market, NeedProduct } from "./lib/types"

interface Props {
  today: string
  markets: Market[]
  products: NeedProduct[]
  destinations_set_up: boolean
}

function matches(product: NeedProduct, query: string): boolean {
  const wanted = query.trim().toLowerCase()
  if (!wanted) return true
  return [product.product_external_id, product.name ?? "", product.sku ?? "", ...product.suppliers]
    .some((text) => text.toLowerCase().includes(wanted))
}

// The Purchase Plan: every product the Inventory page marks Critical or Order
// soon, with how much to order. Read-only. The server sends them most overdue
// first.
export default function PurchaseProductsPage({ today, markets, products, destinations_set_up }: Props) {
  const [query, setQuery] = useState("")
  const shown = products.filter((product) => matches(product, query))

  return (
    <AppLayout
      header={
        <PageTitle
          title="Purchase Plan"
          subtitle={`${products.length} products need buying. All markets.`}
          subtitleExtra={<Link href={purchase_destinations_path()} className="text-link">Incoming order destinations</Link>}
        />
      }
    >
      <div className="flex flex-col gap-4">
        {!destinations_set_up && (
          <div className="rounded-lg border bg-warning-surface px-4 py-3 text-sm">
            Incoming orders are not matched to a market yet, so they are not subtracted anywhere.{" "}
            <Link href={purchase_destinations_path()} className="text-link">Set up destinations</Link>
          </div>
        )}

        <Card>
          <CardHeader>
            <Input
              className="max-w-xs"
              placeholder="Search product, SKU or supplier"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </CardHeader>
          <CardContent>
            <div className="rounded-md border bg-card">
              {shown.length > 0 ? (
                <NeedTable products={shown} markets={markets} today={today} />
              ) : (
                <div className="px-4 py-6 text-center text-sm">
                  {products.length === 0 ? "Nothing needs buying right now." : "No product matches the search."}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
